/**
 * /relationships — the fan's 養成 home. Lists every character the
 * fan has interacted with, sorted newest-first. Each row shows:
 *   - Level badge (rose/orange/amber/emerald/sky/indigo per level)
 *   - 好感度 progress bar within the current level segment
 *   - Streak (🔥 N 天)
 *   - First-person memory card preview
 *   - Milestone count chip
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listMyRelationships,
  type AffinityLevelName,
  type RelationshipListItem,
  type WeeklyDashboard,
} from '../api/fan-api';
import { getStoredFanClaims, fanCognitoConfigured } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const WEEKDAY_LABELS: ReadonlyArray<string> = ['一', '二', '三', '四', '五', '六', '日'];

function dateLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  // ISO weekday 1=Mon..7=Sun. JS getUTCDay returns 0=Sun..6=Sat.
  const dow = d.getUTCDay(); // 0..6
  const isoIdx = (dow + 6) % 7; // 0..6 with Mon=0
  return WEEKDAY_LABELS[isoIdx]!;
}

function StreakDashboard({ dashboard }: { dashboard: WeeklyDashboard }) {
  const todayIso = dashboard.week[dashboard.week.length - 1]?.date;
  return (
    <section className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4 sm:p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400">本週連續登入</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-amber-200 tabular-nums">🔥 {dashboard.longestStreak}</span>
            <span className="text-xs text-slate-400">天</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">當前倍率</div>
          <div className={`mt-1 text-xl font-bold tabular-nums ${
            dashboard.currentMultiplier >= 2 ? 'text-rose-300' :
            dashboard.currentMultiplier >= 1.5 ? 'text-amber-300' :
            dashboard.currentMultiplier >= 1.2 ? 'text-sky-300' : 'text-slate-300'
          }`}>×{dashboard.currentMultiplier.toFixed(1)}</div>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {dashboard.week.map((d) => {
          const isToday = d.date === todayIso;
          return (
            <div
              key={d.date}
              aria-label={`${d.date} ${d.active ? '已互動' : '未互動'}`}
              className={`flex flex-col items-center rounded-lg py-2 text-[10px] transition ${
                d.active
                  ? 'bg-amber-500/25 border border-amber-500/40 text-amber-200'
                  : 'bg-slate-800/60 border border-slate-800 text-slate-600'
              } ${isToday ? 'ring-2 ring-violet-400 ring-inset' : ''}`}
            >
              <span className="text-[10px]">{dateLabel(d.date)}</span>
              <span className="mt-1 text-base leading-none">{d.active ? '✓' : '·'}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">
        每天跟任一角色互動就會繼續累積連續登入；連續 2 天起累加 1.2× / 4 天起 1.5× / 7 天起 2× 好感度倍率。
      </p>
    </section>
  );
}

/** Level → tailwind class fragment for the colored badge + bar. */
const LEVEL_TONE: Record<AffinityLevelName, { bg: string; text: string; bar: string }> = {
  '陌生':       { bg: 'bg-slate-800',        text: 'text-slate-300',   bar: 'bg-slate-500' },
  '點頭之交':   { bg: 'bg-sky-500/20',       text: 'text-sky-300',     bar: 'bg-sky-500' },
  '朋友':       { bg: 'bg-blue-500/20',   text: 'text-blue-300', bar: 'bg-blue-500' },
  '親近':       { bg: 'bg-amber-500/20',     text: 'text-amber-300',   bar: 'bg-amber-500' },
  '摯友':       { bg: 'bg-rose-500/20',      text: 'text-rose-300',    bar: 'bg-rose-500' },
  '靈魂相依':   { bg: 'bg-violet-500/20',    text: 'text-violet-300',  bar: 'bg-violet-500' },
};

const LEVEL_RANGE: ReadonlyArray<readonly [number, number]> = [
  [0, 9], [10, 29], [30, 54], [55, 79], [80, 94], [95, 100],
];

function progressWithinLevel(value: number, level: number): number {
  const [lo, hi] = LEVEL_RANGE[level] ?? [0, 100];
  const span = Math.max(1, hi - lo);
  return Math.max(0, Math.min(1, (value - lo) / span));
}

function SkeletonRow() {
  return (
    <div
      aria-hidden="true"
      className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3"
    >
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full nx-skeleton-shimmer" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 rounded nx-skeleton-shimmer" />
          <div className="h-2 w-full rounded nx-skeleton-shimmer" />
        </div>
      </div>
      <div className="h-3 w-full rounded nx-skeleton-shimmer" />
    </div>
  );
}

export function Relationships() {
  useDocumentTitle('養成');
  const navigate = useNavigate();
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const [items, setItems] = useState<RelationshipListItem[] | null>(null);
  const [dashboard, setDashboard] = useState<WeeklyDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    void listMyRelationships()
      .then((r) => { if (alive) { setItems(r.items); setDashboard(r.dashboard); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [signedIn]);

  if (!signedIn) {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6 sm:py-16">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 space-y-5 text-center">
          <div className="h-16 w-16 rounded-full bg-violet-500/15 border border-violet-500/40 flex items-center justify-center mx-auto text-violet-300 text-3xl">💞</div>
          <h1 className="text-xl sm:text-2xl font-semibold">需要登入</h1>
          <p className="text-slate-400 text-sm">登入後就能看到你跟每位角色的關係進展。</p>
          {fanCognitoConfigured() && (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition"
            >登入 / 註冊</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      <header>
        <div className="text-[11px] uppercase tracking-[0.2em] text-rose-400">養成</div>
        <h1 className="text-xl sm:text-2xl font-semibold mt-1">我的角色關係</h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          每聊一次、做一個小活動，跟角色之間的好感度都會慢慢累積。
        </p>
      </header>

      {err && (
        <div role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
          {err}
        </div>
      )}

      {/* Weekly streak dashboard — only when we have data; brand-new
          fans with no rows see a zero-day dashboard with all 7
          days inactive, which is a fine "start here" state. */}
      {dashboard && <StreakDashboard dashboard={dashboard} />}

      {items === null && (
        <div className="space-y-3" aria-live="polite" aria-busy="true">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {items && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/50 p-6 text-center space-y-3">
          <div className="text-4xl" aria-hidden="true">💌</div>
          <p className="text-slate-300">還沒有任何角色關係</p>
          <p className="text-slate-500 text-xs">先到首頁挑一個喜歡的角色，跟他/她聊聊吧。</p>
          <Link
            to="/"
            className="inline-block rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-medium px-4 py-2 transition"
          >逛角色列表</Link>
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((r, i) => {
            const tone = LEVEL_TONE[r.levelName];
            const pct = progressWithinLevel(r.value, r.level);
            return (
              <li
                key={r.characterId}
                className="nx-card-enter"
                style={{ ['--enter-delay' as string]: `${Math.min(i, 11) * 0.045}s` }}
              >
                <Link
                  to={`/relationships/${encodeURIComponent(r.characterId)}`}
                  aria-label={`${r.characterName} 關係詳情`}
                  className="block rounded-2xl border border-slate-800 bg-slate-900 hover:border-slate-700 active:scale-[0.99] transition p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <div className={`${tone.bg} ${tone.text} h-12 w-12 rounded-full flex flex-col items-center justify-center shrink-0`}>
                      <span className="text-[10px] leading-none">Lv</span>
                      <span className="text-base font-bold leading-none mt-0.5">{r.level}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate">{r.characterName}</div>
                        <span className={`text-[10px] rounded-full px-2 py-0.5 ${tone.bg} ${tone.text} shrink-0`}>{r.levelName}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${tone.bar} transition-[width] duration-500`}
                          style={{ width: `${Math.round(pct * 100)}%` }}
                        />
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 tabular-nums">
                        <span>好感度 {r.value} / 100</span>
                        {r.streakDays > 0 && (
                          <span className="text-amber-300">🔥 {r.streakDays} 天</span>
                        )}
                        {r.milestoneCount > 0 && (
                          <span className="text-violet-300">👑 {r.milestoneCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {r.memoryCardPreview ? (
                    <p className="text-xs text-slate-400 italic line-clamp-2 border-l-2 border-rose-500/40 pl-2">
                      「{r.memoryCardPreview}」
                    </p>
                  ) : (
                    <p className="text-xs text-slate-600 italic">
                      （角色的印象卡會在你們互動幾次後出現）
                    </p>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
